import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { selectMarketplaceDetailed } from "../../store/auth-slice";
import { useConsultOrderContext } from "../../contexts/ConsultOrderContext";
import { UserAddressRequestPayload } from "../../types/consultOrder.types";
import {
  createPlaceAutocompleteElement,
  resolveSelectedPlace,
} from "../../services/placesAutocompleteService";
import { mapPlaceToUserAddress } from "../../utils/mapPlaceToUserAddress";
import styles from "./AddressCreate.module.scss";

// ITAM-2583 (phase-2): context-driven "new address" entry backed by the Google
// Places autocomplete element. It depends ONLY on placesAutocompleteService
// (the injectable seam) — never on the live SDK — so tests mock the element and
// the resolver with no billable/network call.
//
// Correctness guardrails:
//  - Only a RESOLVED prediction (finite, non-zero lat/lng via mapPlaceToUserAddress)
//    is written to draft.newAddress; anything else keeps it null so free text can
//    never become a submittable address.
//  - Editing the field after a selection (the element's `input` event) invalidates
//    the previously resolved geocode (stale-geocode guard) — including a
//    resolution still in flight (discarded via a selection generation token).
//  - On remount the resolved view re-seeds from draft.newAddress, so a draft
//    preserved across form close/reopen stays visible and editable.

const AddressCreate: React.FC = () => {
  const { draft, updateDraft } = useConsultOrderContext();
  const detailed = useSelector(selectMarketplaceDetailed);

  const branchLat = detailed?.latitude;
  const branchLng = detailed?.longitude;

  const hostRef = useRef<HTMLDivElement>(null);
  const [errored, setErrored] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  // Seeded from the draft on (re)mount: the form modal unmounts this component
  // on close while the draft survives (grace window / chip reopen), so a
  // previously resolved address must stay visible and editable — not become an
  // invisible-but-submittable draft value behind an empty search box.
  const [resolvedAddress, setResolvedAddress] =
    useState<UserAddressRequestPayload | null>(() => draft.newAddress);
  // Monotonic token for the LATEST selection/edit. A resolution that finishes
  // after a newer select/input/error is stale and must be discarded, otherwise
  // it writes an old geocode back over the invalidation done by handleInput.
  const selectionGenRef = useRef(0);

  // Create + wire the Places autocomplete element. Re-runs on a retry.
  useEffect(() => {
    let cancelled = false;
    let element: HTMLElement | null = null;
    const host = hostRef.current;

    const handleSelect = async (event: Event) => {
      // Derive the prediction type from the service seam so this component never
      // needs the ambient `google` namespace types directly.
      const placePrediction = (
        event as unknown as {
          placePrediction: Parameters<typeof resolveSelectedPlace>[0];
        }
      ).placePrediction;
      const gen = ++selectionGenRef.current;
      try {
        const resolved = await resolveSelectedPlace(placePrediction);
        const address = mapPlaceToUserAddress(
          resolved.components,
          resolved.lat,
          resolved.lng,
          resolved.formatted
        );
        if (cancelled || gen !== selectionGenRef.current) {
          return;
        }
        setResolvedAddress(address);
        updateDraft({ newAddress: address });
      } catch {
        if (cancelled || gen !== selectionGenRef.current) {
          return;
        }
        // Unresolved (zero/NaN coordinate, etc.): never submittable.
        setResolvedAddress(null);
        updateDraft({ newAddress: null });
      }
    };

    // Editing the field after a selection invalidates the resolved geocode —
    // including any resolution still in flight (the gen bump discards it).
    const handleInput = () => {
      selectionGenRef.current += 1;
      setResolvedAddress(null);
      updateDraft({ newAddress: null });
    };

    const handleError = () => {
      selectionGenRef.current += 1;
      setResolvedAddress(null);
      updateDraft({ newAddress: null });
      setErrored(true);
    };

    const init = async () => {
      try {
        const el = await createPlaceAutocompleteElement({
          branchLat,
          branchLng,
        });
        if (cancelled) {
          return;
        }
        element = el as unknown as HTMLElement;
        element.addEventListener("gmp-select", handleSelect);
        element.addEventListener("gmp-error", handleError);
        element.addEventListener("input", handleInput);
        host?.appendChild(element);
      } catch {
        if (cancelled) {
          return;
        }
        setErrored(true);
      }
    };

    init();

    return () => {
      cancelled = true;
      if (element) {
        element.removeEventListener("gmp-select", handleSelect);
        element.removeEventListener("gmp-error", handleError);
        element.removeEventListener("input", handleInput);
        element.remove();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchLat, branchLng, retryNonce]);

  const retry = () => {
    setErrored(false);
    setRetryNonce((n) => n + 1);
  };

  const patchNewAddress = (patch: Partial<UserAddressRequestPayload>) => {
    if (!resolvedAddress) {
      return;
    }
    const next = { ...resolvedAddress, ...patch };
    setResolvedAddress(next);
    updateDraft({ newAddress: next });
  };

  return (
    <div className={styles.AddressCreate}>
      <span id="address-create-label" className={styles.fieldLabel}>
        Adresa dostave
      </span>
      <p className={styles.hint}>
        Počnite da kucate adresu i izaberite predlog iz liste.
      </p>

      {!errored && (
        <div
          className={styles.autocompleteHost}
          ref={hostRef}
          aria-labelledby="address-create-label"
        />
      )}

      {errored && (
        <div className={styles.guidance} role="alert">
          <p className={styles.guidanceTitle}>
            Pretraga adresa trenutno nije dostupna
          </p>
          <p className={styles.guidanceBody}>
            Nije moguće učitati Google pretragu adresa (dozvola ili mreža).
          </p>
          <button
            type="button"
            className={styles.retryButton}
            onClick={retry}
          >
            Pokušaj ponovo
          </button>
        </div>
      )}

      {resolvedAddress && (
        <div className={styles.resolved}>
          <div className={styles.resolvedSummary}>
            <span className={styles.resolvedStreet}>
              {[resolvedAddress.name, resolvedAddress.number]
                .filter(Boolean)
                .join(" ")}
            </span>
            <span className={styles.resolvedCity}>
              {[resolvedAddress.postalCode, resolvedAddress.city]
                .filter(Boolean)
                .join(" ")}
            </span>
          </div>

          <div className={styles.manualGrid}>
            <label className={styles.manualField}>
              <span className={styles.fieldLabel}>Broj</span>
              <input
                className={styles.input}
                type="text"
                value={resolvedAddress.number ?? ""}
                onChange={(e) => patchNewAddress({ number: e.target.value })}
              />
            </label>
            <label className={styles.manualField}>
              <span className={styles.fieldLabel}>Stan</span>
              <input
                className={styles.input}
                type="text"
                value={resolvedAddress.apartment ?? ""}
                onChange={(e) =>
                  patchNewAddress({ apartment: e.target.value })
                }
              />
            </label>
            <label className={styles.manualField}>
              <span className={styles.fieldLabel}>Sprat</span>
              <input
                className={styles.input}
                type="text"
                value={resolvedAddress.floor ?? ""}
                onChange={(e) => patchNewAddress({ floor: e.target.value })}
              />
            </label>
            <label className={styles.manualField}>
              <span className={styles.fieldLabel}>Napomena</span>
              <input
                className={styles.input}
                type="text"
                value={resolvedAddress.otherInformation ?? ""}
                onChange={(e) =>
                  patchNewAddress({ otherInformation: e.target.value })
                }
              />
            </label>
          </div>
        </div>
      )}

      <label className={styles.saveRow}>
        <input
          type="checkbox"
          checked={draft.saveNewAddress}
          onChange={(e) => updateDraft({ saveNewAddress: e.target.checked })}
        />
        <span>Sačuvaj ovu adresu u profil kupca (uz saglasnost kupca)</span>
      </label>
    </div>
  );
};

export default AddressCreate;
